import { recordAction } from "@/lib/actionLedger";
import { db } from "@/lib/firebase";
import { Domain, parseDomain } from "@/schemas/firestore/domain.schema";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

/* ---------- slice ---------- */
export interface DomainSlice {
  /* state */
  domains: Domain[];
  currentDomain: Domain | null;
  isLoadingDomains: boolean;
  /**
   * Whether a domains snapshot has actually arrived for the current org.
   *
   * `domains: []` alone can't answer "does this org have any domains?" -- it is
   * also the state before the listener starts. `isLoadingDomains` can't either,
   * since `deleteDomain` reuses it as a generic busy flag. Set only by a
   * successful snapshot, never by the error path: a failed listener means
   * unknown, not zero.
   */
  hasLoadedDomains: boolean;
  domainError: string | null;

  /* actions */
  subscribeToDomains: (orgId: string) => () => void;
  setCurrentDomain: (domainId: string | null) => Promise<void>;
  addDomain: (orgId: string, url: string) => Promise<string>;
  deleteDomain: (orgId: string, domainId: string) => Promise<void>;
  unsubscribeDomains: () => void;

  /* internals */
  _domainUnsubscribe: (() => void) | null;
}

// Create the Domain slice
export const createDomainSlice: StateCreator<
  StoreState,
  [],
  [],
  DomainSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  domains: [],
  currentDomain: null,
  isLoadingDomains: false,
  hasLoadedDomains: false,
  domainError: null,
  _domainUnsubscribe: null,

  /* ---------- actions ---------- */
  /** realtime domains list */
  subscribeToDomains: (orgId) => {
    if (!orgId) {
      console.log("Domains subscription requires orgId.");
      return () => {};
    }

    // Clean up any existing subscription before creating a new one
    get()._domainUnsubscribe?.();
    set({
      domains: [],
      isLoadingDomains: true,
      hasLoadedDomains: false,
      domainError: null,
    });

    console.log(`Setting up real-time domains listener for org ${orgId}`);
    const domainsRef = collection(db, `organizations/${orgId}/domains`);
    const q = query(domainsRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const domainsList: Domain[] = [];

        for (const docSnap of snapshot.docs) {
          try {
            const domain = parseDomain({
              id: docSnap.id,
              // A just-created domain's serverTimestamp() is null until the server
              // acks; "estimate" substitutes the local time so it parses.
              ...docSnap.data({ serverTimestamps: "estimate" }),
            });
            domainsList.push(domain);
          } catch (error) {
            // Skip the invalid doc so one bad domain doesn't break the whole list
            console.error(`Domain validation failed for document ${docSnap.id}:`, {
              error: error instanceof Error ? error.message : error,
              rawData: docSnap.data(),
            });
          }
        }

        console.log(
          `Domains snapshot received with ${snapshot.docs.length} documents`,
        );

        // Update domains list in store
        set({
          domains: domainsList,
          isLoadingDomains: false,
          hasLoadedDomains: true,
        });

        // Update current domain object if needed and sync header tools
        const currentDomainId = get().userData?.currentDomainId;
        const existingCurrentDomain = get().currentDomain;

        if (currentDomainId) {
          const updatedCurrentDomain = domainsList.find(
            (d) => d.id === currentDomainId,
          );

          // Check if the found domain is different from the existing currentDomain
          // or if the existingCurrentDomain was null (initial load case)
          if (
            updatedCurrentDomain &&
            (!existingCurrentDomain ||
              JSON.stringify(updatedCurrentDomain) !==
                JSON.stringify(existingCurrentDomain))
          ) {
            console.log("DomainSlice: Updating currentDomain from snapshot.");
            set({ currentDomain: updatedCurrentDomain });
            // Reset header tools ONLY if the current domain data actually changed or loaded
            get().resetHeaderTools?.();
          } else if (!updatedCurrentDomain && existingCurrentDomain) {
            // Current domain ID exists but domain is gone (e.g., deleted)
            console.log("DomainSlice: Current domain deleted, clearing state.");
            set({ currentDomain: null });
            get().resetHeaderTools?.(); // Clear selections
          }
          // If updatedCurrentDomain is the same as existingCurrentDomain, do nothing.
        } else if (existingCurrentDomain) {
          // No currentDomainId set (e.g., user logged out/changed), but there was a currentDomain
          console.log(
            "DomainSlice: No currentDomainId, clearing currentDomain.",
          );
          set({ currentDomain: null });
          get().resetHeaderTools?.(); // Clear selections
        }
        // If no currentDomainId and no existingCurrentDomain, do nothing.
      },
      (error) => {
        console.error("Domains subscription error:", error);
        set({
          domainError: "Failed to subscribe to domains",
          isLoadingDomains: false,
        });
      },
    );

    set({ _domainUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  /** change active domain + sync header checkboxes */
  setCurrentDomain: async (domainId) => {
    if (!domainId) {
      set({ currentDomain: null });
      get().resetHeaderTools?.(); // clear selections
      // We no longer unsubscribe from pages here since we're subscribing to all domains' pages
      return;
    }

    const existingDomain = get().domains.find((d: Domain) => d.id === domainId);
    if (existingDomain) {
      // Callers re-run this on every domains snapshot (the id can resolve late,
      // after the domains list loads), so only treat it as a *switch* when the
      // id actually changed -- resetHeaderTools wipes the user's selections.
      const isDomainSwitch = get().currentDomain?.id !== existingDomain.id;
      set({ currentDomain: existingDomain });
      if (isDomainSwitch) {
        get().resetHeaderTools?.(); //  align checkboxes with Firestore
      }
      // Pages for all domains are already subscribed in subscribeToDomains
    } else {
      console.warn(
        `setCurrentDomain: Domain ID ${domainId} not found in the loaded domains list.`,
      );
      // Ensure currentDomain is cleared if the ID is invalid
      if (get().currentDomain?.id === domainId) {
        set({ currentDomain: null });
        get().resetHeaderTools?.(); // Clear selections if the invalid domain was the current one
      }
    }
  },

  // Add a new domain
  addDomain: async (orgId, url) => {
    if (!orgId) {
      set({ domainError: "Organization ID is required to add a domain." });
      throw new Error("Organization ID is required to add a domain.");
    }
    try {
      const domainsRef = collection(db, `organizations/${orgId}/domains`);
      // Pre-generate the doc ref so `id` and `org_id` can be denormalized
      // onto the document itself -- the backend's find_domain_org_id does a
      // collection-group query across all orgs' domains subcollections and
      // can only filter on document *data* (not the trailing path segment),
      // so every domain-scoped backend operation depends on these fields
      // being present.
      const newDomainRef = doc(domainsRef);
      // `created_by` is pinned to request.auth.uid by firestore.rules, so this
      // cannot attribute the write to anyone else -- and the write is rejected
      // outright if the field is missing.
      await setDoc(newDomainRef, {
        id: newDomainRef.id,
        org_id: orgId,
        url: url,
        created_at: serverTimestamp(),
        created_by: get().currentUser?.uid ?? "",
      });
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "domain.created",
        targetPath: newDomainRef.path,
        domainId: newDomainRef.id,
        summary: url,
      });
      return newDomainRef.id; // Return the new domain ID
    } catch (error) {
      console.error("Error adding domain:", error);
      set({ domainError: "Failed to add domain" });
      throw new Error("Failed to add domain");
    }
  },

  // Delete a domain and its subcollections (pages, jobs)
  deleteDomain: async (orgId, domainId) => {
    if (!orgId || !domainId) return;

    set({ isLoadingDomains: true }); // Indicate loading
    try {
      const domainRef = doc(db, `organizations/${orgId}/domains`, domainId);
      const pagesRef = collection(domainRef, "pages");

      const batch = writeBatch(db);

      // Delete pages and their jobs (assuming jobs are under pages)
      const pagesQuery = query(pagesRef);
      const pagesDocs = await getDocs(pagesQuery); // Use getDocs here

      for (const pageDoc of pagesDocs.docs) {
        const pageRef = doc(
          db,
          `organizations/${orgId}/domains/${domainId}/pages`,
          pageDoc.id,
        );
        // TODO: Add deletion logic for jobs within each page if necessary
        batch.delete(pageRef); // Delete the page itself
      }

      // Delete the domain document
      batch.delete(domainRef);

      // Recorded before the batch commits: once the document is gone there is
      // no `created_by` left to read, and the deleter would be unattributable.
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "domain.deleted",
        targetPath: domainRef.path,
        domainId,
        summary: get().currentDomain?.url ?? domainId,
      });

      await batch.commit();

      // Optimistic update handled by subscription, but clear currentDomain if needed
      if (get().currentDomain?.id === domainId) {
        set({ currentDomain: null, isLoadingDomains: false });
        get().resetHeaderTools?.(); // Clear selections
      } else {
        set({ isLoadingDomains: false });
      }

      // If the deleted domain was the current one in user data, update user doc
      const currentUser = get().currentUser;
      if (currentUser && get().userData?.currentDomainId === domainId) {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { currentDomainId: "", currentPageId: "" });
        // Also trigger updates in other slices via userSlice listener
      }
    } catch (error) {
      console.error("Error deleting domain:", error);
      set({ domainError: "Failed to delete domain", isLoadingDomains: false });
    }
  },

  // Manually unsubscribe from domains listener
  unsubscribeDomains: () => {
    const unsubscribe = get()._domainUnsubscribe;
    if (unsubscribe) {
      console.log("Unsubscribing from domains listener.");
      unsubscribe();
    }
    // Drop the data along with the listener. Detaching alone left the previous
    // session's list -- and, worse, its `hasLoadedDomains: true` -- in the store
    // across a sign-out, so the next account rendered against them: an empty
    // list flagged as loaded is indistinguishable from a confirmed "this org has
    // no domains", which is exactly the input EmailVerificationGuard turns into
    // a setup-wizard redirect. Mirrors `clearOrganization`, which is called
    // alongside this on sign-out.
    set({
      domains: [],
      hasLoadedDomains: false,
      domainError: null,
      _domainUnsubscribe: null,
    });
  },
});
